import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";
import { Icon } from "../app/Icon";
import { FunctionsError } from "../lib/functions-client";
import { getPayments, openPortal, money, type PaymentMethod, type Charge } from "../lib/billing";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import {
  connectOnboard, connectStatus, confirmPlacement, placementStatus, feeUsd, type Placement,
} from "../lib/placements";

export function Payments() {
  const { account } = useAuth();
  const { profiles } = useProfiles();
  const isRecruiter = profiles.some((p) => p.type === "recruiter");
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [charges, setCharges] = useState<Charge[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [placementsEnabled, setPlacementsEnabled] = useState(false);
  const [onboarded, setOnboarded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [placeBusy, setPlaceBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    getPayments()
      .then((r) => { setMethods(r.methods); setCharges(r.charges); })
      .catch(() => undefined)
      .finally(() => setLoading(false));
    placementStatus().then((r) => { setPlacements(r.placements); setPlacementsEnabled(r.enabled); }).catch(() => undefined);
    if (isRecruiter) connectStatus().then((r) => setOnboarded(r.onboarded)).catch(() => undefined);
  }, [isRecruiter]);

  async function onboard() {
    setBusy(true);
    setNote(null);
    try {
      const { url } = await connectOnboard();
      window.location.href = url;
    } catch (e) {
      setNote(e instanceof FunctionsError && e.status === 501 ? "Payouts aren't enabled yet." : "Could not start payout onboarding.");
      setBusy(false);
    }
  }

  async function confirmSide(p: Placement) {
    if (!p.job_application_id) return;
    setPlaceBusy(p.id);
    setNote(null);
    try {
      await confirmPlacement(p.job_application_id);
      const r = await placementStatus();
      setPlacements(r.placements);
    } catch {
      setNote("Could not confirm the placement.");
    } finally {
      setPlaceBusy(null);
    }
  }
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(charges, 10, "list");

  async function manage() {
    setBusy(true);
    setNote(null);
    try {
      const { url } = await openPortal();
      window.location.href = url;
    } catch (e) {
      setNote(e instanceof FunctionsError ? e.message : "Could not open the billing portal.");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader title="Payments" subtitle="Payment methods and charge history." />

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <>
          <Panel
            title="Payment methods"
            action={methods.length > 0 ? <Button variant="ghost" disabled={busy} onClick={manage}>Manage in Stripe portal</Button> : undefined}
          >
            {methods.length === 0 ? (
              <p className="text-sm text-muted">
                No payment method on file. <Link to="/billing" className="font-bold text-accent-strong">Subscribe to Pro →</Link>
              </p>
            ) : (
              <ul className="grid">
                {methods.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 border-t border-line py-3 first:border-t-0 first:pt-0">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted"><Icon name="card" className="h-4 w-4" /></span>
                    <span className="flex-1 font-mono text-sm">
                      <span className="font-bold uppercase">{m.brand}</span> •••• {m.last4}
                    </span>
                    {m.exp_month && <span className="font-mono text-xs text-muted">exp {String(m.exp_month).padStart(2, "0")}/{m.exp_year}</span>}
                  </li>
                ))}
              </ul>
            )}
            {note && <p className="font-mono text-sm text-danger">{note}</p>}
          </Panel>

          <Panel title="Charge history" action={charges.length > 0 ? <ViewToggle view={view} onChange={setView} /> : undefined}>
            {charges.length === 0 ? (
              <p className="text-sm text-muted">No payments yet.</p>
            ) : view === "list" ? (
              <div className="overflow-x-auto">
                <div className="min-w-[520px]">
                  <div className="grid grid-cols-[120px_1fr_100px_90px] items-center gap-4 border-b border-line pb-2 text-[11px] font-bold uppercase tracking-widest text-muted">
                    <span>Date</span><span>Description</span><span className="justify-self-end">Amount</span><span>Status</span>
                  </div>
                  {pageItems.map((c) => (
                    <div key={c.id} className="grid grid-cols-[120px_1fr_100px_90px] items-center gap-4 border-t border-line py-3">
                      <span className="font-mono text-xs text-muted">{new Date(c.created * 1000).toLocaleDateString()}</span>
                      <span className="truncate text-sm">{c.description}</span>
                      <span className="justify-self-end font-mono text-sm font-bold">{money(c.amount, c.currency)}</span>
                      <StatusLabel status={c.status === "succeeded" ? "paid" : c.status} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {pageItems.map((c) => (
                  <div key={c.id} className="grid gap-2 rounded-card border border-line bg-surface-2 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-bold">{money(c.amount, c.currency)}</span>
                      <StatusLabel status={c.status === "succeeded" ? "paid" : c.status} />
                    </div>
                    <span className="truncate text-sm text-muted">{c.description}</span>
                    <span className="font-mono text-[10px] text-muted">{new Date(c.created * 1000).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
            <Pagination page={page} pageCount={pageCount} onPage={setPage} />
          </Panel>

          {/* Placement fees & payouts (Phase 2 slice C, ADR-0020) — dark until enabled */}
          <Panel
            title="Placement fees"
            action={isRecruiter && !onboarded ? <Button variant="ghost" disabled={busy} onClick={onboard}>Set up payouts</Button> : undefined}
          >
            {!placementsEnabled && (
              <p className="rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-muted">
                Placement fees are not active yet. You can confirm hires; no fee is charged until we turn this on.
              </p>
            )}
            {isRecruiter && (
              <p className="text-sm text-muted">
                {onboarded ? "Payouts are set up — you'll receive your share of placement fees here." : "Set up payouts to receive your share when a hire you made is confirmed."}
              </p>
            )}
            {placements.length === 0 ? (
              <p className="text-sm text-muted">No placements yet. When a hire is confirmed by both sides, it appears here.</p>
            ) : (
              <ul className="grid gap-2 border-t border-line pt-3">
                {placements.map((p) => {
                  const iAmCompany = account?.id === p.company_account_id;
                  const iAmDriver = account?.id === p.driver_account_id;
                  const myConfirmPending = (iAmCompany && !p.company_confirmed) || (iAmDriver && !p.driver_confirmed);
                  return (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 font-mono text-sm">
                      <span className="flex items-center gap-3">
                        <StatusLabel status={p.state === "settled" ? "paid" : p.state === "held" ? "processing" : p.state} />
                        <span className="text-ink">{feeUsd(p.fee_cents)}</span>
                        {p.recruiter_account_id && account?.id === p.recruiter_account_id && (
                          <span className="text-muted">your net {feeUsd(p.recruiter_net_cents)}</span>
                        )}
                      </span>
                      <span className="flex items-center gap-2 text-[11px] text-muted">
                        <span>{p.company_confirmed ? "company ✓" : "company —"} · {p.driver_confirmed ? "driver ✓" : "driver —"}</span>
                        {myConfirmPending && p.state === "pending" && (
                          <Button variant="ghost" disabled={placeBusy === p.id} onClick={() => confirmSide(p)}>
                            {placeBusy === p.id ? "…" : "Confirm your side"}
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
